import React, { useState, useEffect, useRef } from 'react';
import { useLanguage } from '../LanguageContext';
import { CONTENT } from '../content';
import { GoogleGenAI, LiveServerMessage, Modality } from "@google/genai";

interface VoiceAIModalProps {
  isOpen: boolean;
  onClose: () => void;
}

const VOICES = [
  { name: 'Zephyr', label: 'Zephyr' },
  { name: 'Puck', label: 'Puck' },
  { name: 'Charon', label: 'Charon' },
  { name: 'Kore', label: 'Kore' },
  { name: 'Fenrir', label: 'Fenrir' },
];

// --- System Instruction Generator ---
const generateSystemInstruction = (lang: 'en' | 'es') => {
  const c = CONTENT[lang];
  
  return `
    You are the Suncoast MD Virtual Assistant.
    
    YOUR MISSION:
    ${c.common.footerDesc}
    
    PRIMARY SERVICES (THE "TWO LANES"):
    1. ${c.home.lane1Title}: ${c.home.lane1Desc}
    2. ${c.home.lane2Title}: ${c.home.lane2Desc}
    
    KEY VALUE PROPOSITIONS:
    ${c.home.pillars.map(p => `- ${p.title}: ${p.desc}`).join('\n')}
    
    FREQUENTLY ASKED QUESTIONS & ANSWERS:
    ${c.home.faq.items.map(i => `Q: ${i.question}\nA: ${i.answer}`).join('\n')}
    
    CONTACT INFO:
    Phone: ${c.contact.info.phone}
    Email: ${c.contact.info.email}
    Headquarters: ${c.contact.info.hq}
    
    BEHAVIOR:
    - You represent Suncoast MD Partners.
    - You are talking to independent physicians or practice managers.
    - Explain the difference between Shared Savings (Upside) and Capitation (Risk) clearly if asked.
    - Be professional, encouraging, and concise.
    - Do NOT provide medical advice.
    - Keep responses short (2-3 sentences max) to maintain a natural conversation flow.
    - If you don't know an answer, suggest booking a "Performance Check" call.
  `;
};

// --- Audio Helpers ---

function encode(bytes: Uint8Array) {
  let binary = '';
  const len = bytes.byteLength;
  for (let i = 0; i < len; i++) {
    binary += String.fromCharCode(bytes[i]);
  }
  return btoa(binary);
}

function decode(base64: string) {
  const binaryString = atob(base64);
  const len = binaryString.length;
  const bytes = new Uint8Array(len);
  for (let i = 0; i < len; i++) {
    bytes[i] = binaryString.charCodeAt(i);
  }
  return bytes;
}

// IMPORTANT: Downsamples audio from browser rate (e.g. 48000) to target rate (16000)
// This fixes the issue where Gemini hears "slow motion" or nothing at all on Production.
function downsampleBuffer(buffer: Float32Array, inputRate: number, outputRate: number): Float32Array {
    if (outputRate === inputRate) {
        return buffer;
    }
    const ratio = inputRate / outputRate;
    const newLength = Math.round(buffer.length / ratio);
    const result = new Float32Array(newLength);
    let offsetResult = 0;
    let offsetBuffer = 0;
    
    while (offsetResult < result.length) {
        const nextOffsetBuffer = Math.round((offsetResult + 1) * ratio);
        let accum = 0, count = 0;
        for (let i = offsetBuffer; i < nextOffsetBuffer && i < buffer.length; i++) {
            accum += buffer[i];
            count++;
        }
        result[offsetResult] = count > 0 ? accum / count : 0;
        offsetResult++;
        offsetBuffer = nextOffsetBuffer;
    }
    return result;
}

function createPcmData(data: Float32Array) {
  const l = data.length;
  const int16 = new Int16Array(l);
  for (let i = 0; i < l; i++) {
    const s = Math.max(-1, Math.min(1, data[i]));
    int16[i] = s < 0 ? s * 0x8000 : s * 0x7FFF;
  }
  return {
    data: encode(new Uint8Array(int16.buffer)),
    mimeType: 'audio/pcm;rate=16000',
  };
}

async function decodeAudioData(
  data: Uint8Array,
  ctx: AudioContext,
  sampleRate: number,
  numChannels: number,
): Promise<AudioBuffer> {
  const dataInt16 = new Int16Array(data.buffer);
  const frameCount = dataInt16.length / numChannels;
  const buffer = ctx.createBuffer(numChannels, frameCount, sampleRate);

  for (let channel = 0; channel < numChannels; channel++) {
    const channelData = buffer.getChannelData(channel);
    for (let i = 0; i < frameCount; i++) {
      channelData[i] = dataInt16[i * numChannels + channel] / 32768.0;
    }
  }
  return buffer;
}

const VoiceAIModal: React.FC<VoiceAIModalProps> = ({ isOpen, onClose }) => {
  const { language } = useLanguage();
  const t = CONTENT[language].voiceAI;
  const commonT = CONTENT[language].common;
  const scrollRef = useRef<HTMLDivElement>(null);

  const [status, setStatus] = useState<'idle' | 'connected' | 'speaking' | 'error'>('idle');
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [transcript, setTranscript] = useState<string[]>([]);
  const [selectedVoice, setSelectedVoice] = useState('Zephyr');
  
  const inputAudioContextRef = useRef<AudioContext | null>(null);
  const outputAudioContextRef = useRef<AudioContext | null>(null);
  const mediaStreamRef = useRef<MediaStream | null>(null);
  const processorRef = useRef<ScriptProcessorNode | null>(null);
  const sourceRef = useRef<MediaStreamAudioSourceNode | null>(null);
  const sessionRef = useRef<Promise<any> | null>(null);
  const nextStartTimeRef = useRef<number>(0);
  const audioSourcesRef = useRef<Set<AudioBufferSourceNode>>(new Set());
  
  const currentInputTranscription = useRef('');
  const currentOutputTranscription = useRef('');

  useEffect(() => {
    if (isOpen) {
      if (transcript.length === 0) {
        setTranscript([t.initialGreeting]);
      }
    } else {
       disconnect();
    }
  }, [isOpen]);

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [transcript, status]);

  useEffect(() => {
    return () => {
      disconnect();
    };
  }, []);

  const disconnect = () => {
    if (sessionRef.current) {
      sessionRef.current = null;
    }

    if (mediaStreamRef.current) {
      mediaStreamRef.current.getTracks().forEach(track => track.stop());
      mediaStreamRef.current = null;
    }

    if (processorRef.current) {
      processorRef.current.disconnect();
      processorRef.current = null;
    }
    if (sourceRef.current) {
      sourceRef.current.disconnect();
      sourceRef.current = null;
    }

    if (inputAudioContextRef.current && inputAudioContextRef.current.state !== 'closed') {
      inputAudioContextRef.current.close().catch(console.error);
      inputAudioContextRef.current = null;
    }
    if (outputAudioContextRef.current && outputAudioContextRef.current.state !== 'closed') {
      outputAudioContextRef.current.close().catch(console.error);
      outputAudioContextRef.current = null;
    }
    
    audioSourcesRef.current.forEach(source => {
        try { source.stop(); } catch(e) {}
    });
    audioSourcesRef.current.clear();

    if (status !== 'error') setStatus('idle');
    nextStartTimeRef.current = 0;
  };

  const startSession = async () => {
    if (status === 'connected' || status === 'speaking') {
        disconnect();
        return;
    }

    setErrorMessage(null);

    try {
      // Direct access thanks to vite.config.ts definition
      const apiKey = process.env.API_KEY;
      
      if (!apiKey) {
        throw new Error("System error: API Key is missing in this environment.");
      }

      setStatus('connected');
      
      const ai = new GoogleGenAI({ apiKey });
      
      const AudioContextClass = window.AudioContext || (window as any).webkitAudioContext;
      // We request 16000, but browser usually ignores it and gives hardware rate (e.g. 48000)
      inputAudioContextRef.current = new AudioContextClass({ sampleRate: 16000 });
      outputAudioContextRef.current = new AudioContextClass({ sampleRate: 24000 });
      
      if (inputAudioContextRef.current.state === 'suspended') {
        await inputAudioContextRef.current.resume();
      }
      if (outputAudioContextRef.current.state === 'suspended') {
        await outputAudioContextRef.current.resume();
      }

      const outputNode = outputAudioContextRef.current.createGain();
      outputNode.connect(outputAudioContextRef.current.destination);

      const systemInstruction = generateSystemInstruction(language);

      const sessionPromise = ai.live.connect({
        model: 'gemini-2.5-flash-native-audio-preview-12-2025',
        config: {
          responseModalities: [Modality.AUDIO],
          speechConfig: {
            voiceConfig: { prebuiltVoiceConfig: { voiceName: selectedVoice } },
          },
          systemInstruction: systemInstruction,
          inputAudioTranscription: {}, 
          outputAudioTranscription: {}, 
        },
        callbacks: {
          onopen: async () => {
            console.log("Gemini Live Session Opened");
            
            try {
                const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
                mediaStreamRef.current = stream;
                
                if (!inputAudioContextRef.current) return;

                const source = inputAudioContextRef.current.createMediaStreamSource(stream);
                sourceRef.current = source;
                
                // Use a larger buffer size for better downsampling processing
                const processor = inputAudioContextRef.current.createScriptProcessor(4096, 1, 1);
                processorRef.current = processor;

                processor.onaudioprocess = (e) => {
                    if (!sessionRef.current) return; 
                    
                    const inputData = e.inputBuffer.getChannelData(0);
                    
                    // CRITICAL FIX: Downsample to 16000Hz before sending
                    // Browser context might be 48000Hz even if we asked for 16000Hz
                    const currentRate = inputAudioContextRef.current?.sampleRate || 48000;
                    const downsampledData = downsampleBuffer(inputData, currentRate, 16000);
                    
                    const pcmData = createPcmData(downsampledData);
                    
                    sessionPromise.then(session => {
                        session.sendRealtimeInput({ media: pcmData });
                    }).catch(e => console.error("Send Error:", e));
                };

                source.connect(processor);
                processor.connect(inputAudioContextRef.current.destination);

            } catch (err) {
                console.error("Mic Error:", err);
                setErrorMessage("Microphone access denied. Please check permissions.");
                setStatus('error');
                disconnect();
            }
          },
          onmessage: async (message: LiveServerMessage) => {
             const serverContent = message.serverContent;
             
             if (serverContent?.modelTurn) {
                setStatus('speaking');
             }

             if (serverContent?.outputTranscription) {
                currentOutputTranscription.current += serverContent.outputTranscription.text;
             } else if (serverContent?.inputTranscription) {
                currentInputTranscription.current += serverContent.inputTranscription.text;
             }

             if (serverContent?.turnComplete) {
                 const userText = currentInputTranscription.current;
                 const aiText = currentOutputTranscription.current;

                 if (userText.trim() || aiText.trim()) {
                     setTranscript(prev => {
                         const newHistory = [...prev];
                         if (userText.trim()) newHistory.push(`You: ${userText}`);
                         if (aiText.trim()) newHistory.push(`AI: ${aiText}`);
                         return newHistory;
                     });
                 }
                 
                 currentInputTranscription.current = '';
                 currentOutputTranscription.current = '';
             }

             const base64Audio = serverContent?.modelTurn?.parts?.[0]?.inlineData?.data;
             if (base64Audio && outputAudioContextRef.current) {
                 const ctx = outputAudioContextRef.current;
                 
                 const audioBuffer = await decodeAudioData(
                     decode(base64Audio),
                     ctx,
                     24000,
                     1
                 );
                 
                 nextStartTimeRef.current = Math.max(
                     nextStartTimeRef.current,
                     ctx.currentTime
                 );
                 
                 const source = ctx.createBufferSource();
                 source.buffer = audioBuffer;
                 source.connect(outputNode);
                 
                 source.addEventListener('ended', () => {
                     audioSourcesRef.current.delete(source);
                     if (audioSourcesRef.current.size === 0) {
                         setStatus(s => s === 'speaking' ? 'connected' : s);
                     }
                 });
                 
                 source.start(nextStartTimeRef.current);
                 nextStartTimeRef.current += audioBuffer.duration;
                 audioSourcesRef.current.add(source);
             }

             if (serverContent?.interrupted) {
                 audioSourcesRef.current.forEach(s => s.stop());
                 audioSourcesRef.current.clear();
                 nextStartTimeRef.current = 0;
                 setStatus('connected');
                 currentOutputTranscription.current = ''; 
             }
          },
          onclose: () => {
            console.log("Session Closed");
            if (status !== 'error') setStatus('idle');
          },
          onerror: (err) => {
            console.error("Gemini Error:", err);
            setErrorMessage("Connection to AI failed. Please try again.");
            setStatus('error');
          }
        }
      });
      
      sessionRef.current = sessionPromise;

    } catch (e: any) {
      console.error("Start Session Failed", e);
      setErrorMessage(e.message || "Failed to start AI session.");
      setStatus('error');
    }
  };

  if (!isOpen) return null;

  const isLive = status === 'connected' || status === 'speaking';

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-suncoast-dark/70 backdrop-blur-sm transition-opacity duration-300">
      <div className="bg-white rounded-3xl w-full max-w-lg shadow-2xl overflow-hidden flex flex-col max-h-[85vh] border border-white/20">
        {/* Header */}
        <div className="bg-suncoast-primary p-5 flex justify-between items-center text-white shadow-md z-10">
          <div className="flex items-center gap-3">
            <div className={`w-3 h-3 rounded-full ${isLive ? 'bg-red-400 animate-pulse' : (status === 'error' ? 'bg-orange-400' : 'bg-gray-400')}`}></div>
            <div>
               <h3 className="font-bold text-lg leading-none">{t.title}</h3>
               <p className="text-xs text-suncoast-bg opacity-80 font-medium">
                   {status === 'speaking' ? 'Speaking...' : (status === 'connected' ? 'Listening...' : (status === 'error' ? 'Connection Error' : 'Ready to Connect'))}
               </p>
            </div>
          </div>
          <button onClick={onClose} className="text-white/80 hover:text-white bg-white/10 hover:bg-white/20 rounded-full p-2 transition-colors">
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Chat Area */}
        <div ref={scrollRef} className="flex-grow p-6 overflow-y-auto bg-gray-50 space-y-6 scroll-smooth">
          {transcript.map((line, idx) => {
            const isAi = line.startsWith('AI:') || line.startsWith('IA:') || line.startsWith('Suncoast:');
            const content = line.replace(/^(AI:|IA:|You:|Usted:|Suncoast:)\s*/, '');
            return (
              <div key={idx} className={`flex ${isAi ? 'justify-start' : 'justify-end'}`}>
                 <div className={`max-w-[85%] p-4 rounded-2xl text-sm leading-relaxed shadow-sm ${
                   isAi 
                   ? 'bg-white text-gray-800 border border-gray-100 rounded-tl-none' 
                   : 'bg-suncoast-bg text-suncoast-dark font-medium rounded-tr-none'
                 }`}>
                  <span className="font-bold block mb-1 text-xs opacity-50 uppercase tracking-wider">{isAi ? 'Suncoast AI' : 'You'}</span>
                  {content}
                </div>
              </div>
            );
          })}
          
          {/* Visualizer for Speaking State */}
          {status === 'speaking' && (
             <div className="flex justify-start">
               <div className="bg-white border border-gray-100 p-4 rounded-2xl rounded-tl-none shadow-sm flex gap-1.5 items-center">
                  <div className="w-1 h-3 bg-suncoast-primary rounded-full animate-[bounce_1s_infinite_0ms]"></div>
                  <div className="w-1 h-5 bg-suncoast-primary rounded-full animate-[bounce_1s_infinite_200ms]"></div>
                  <div className="w-1 h-3 bg-suncoast-primary rounded-full animate-[bounce_1s_infinite_400ms]"></div>
               </div>
            </div>
          )}

          {/* Error Message Display */}
          {status === 'error' && errorMessage && (
            <div className="flex justify-center">
              <div className="bg-red-50 text-red-600 border border-red-100 p-4 rounded-xl text-sm text-center max-w-[90%]">
                <p className="font-bold mb-1">Connection Issue</p>
                <p>{errorMessage}</p>
                <button onClick={startSession} className="mt-2 text-xs underline font-bold">Try Again</button>
              </div>
            </div>
          )}
        </div>

        {/* Controls */}
        <div className="p-5 border-t border-gray-100 bg-white shadow-[0_-4px_6px_-1px_rgba(0,0,0,0.05)] z-10">
          
          <div className="flex flex-col items-center">
             
             {/* Voice Selection - Only show when not live */}
             {!isLive && (
                <div className="mb-4">
                  <label htmlFor="voice-select" className="sr-only">Choose AI Voice</label>
                  <select 
                    id="voice-select"
                    value={selectedVoice}
                    onChange={(e) => setSelectedVoice(e.target.value)}
                    className="text-xs font-bold text-gray-500 bg-gray-100 border-none rounded-full px-3 py-1.5 cursor-pointer hover:bg-gray-200 focus:ring-0"
                  >
                    {VOICES.map(v => (
                      <option key={v.name} value={v.name}>{v.label}</option>
                    ))}
                  </select>
                </div>
             )}

             <button 
              onClick={startSession}
              disabled={false}
              className={`w-20 h-20 rounded-full flex items-center justify-center transition-all duration-300 ${
                isLive
                ? 'bg-red-500 scale-110 shadow-lg shadow-red-200 ring-4 ring-red-100' 
                : (status === 'error' ? 'bg-gray-400' : 'bg-gradient-to-br from-suncoast-primary to-teal-700 shadow-lg hover:shadow-xl hover:scale-105')
              }`}
            >
              {isLive ? (
                 <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                   <rect x="6" y="6" width="12" height="12" rx="1" strokeWidth="2" fill="currentColor"/>
                 </svg>
              ) : (
                <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 11a7 7 0 01-7 7m0 0a7 7 0 01-7-7m7 7v4m0 0H8m4 0h4m-4-8a3 3 0 01-3-3V5a3 3 0 116 0v6a3 3 0 01-3 3z" />
                </svg>
              )}
            </button>
            <p className="text-xs font-bold text-gray-400 mt-4 text-center uppercase tracking-widest">
              {status === 'speaking' ? 'AI Speaking...' : (status === 'connected' ? 'Listening...' : (status === 'error' ? 'Tap to Retry' : 'Tap to Start Conversation'))}
            </p>
            <p className="text-[10px] text-gray-300 mt-2 text-center">
              {commonT.privacyNote}
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default VoiceAIModal;